import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ApiCallsService } from '../../../../shared/services/api-calls.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { FormSetupComponent } from '../../../../shared/component/form-setup/form-setup.component';
import { TableComponent } from '../../../../shared/component/table/table.component';
import { AgGridTableService } from '../../../../shared/services/ag-grid.service';
import { TableSchemaService } from '../../../../shared/services/table-schema';
import Swal from 'sweetalert2';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

(pdfMake as any).vfs = (pdfFonts as any).vfs;

interface CouponTab {
  id: string;
  name: string;
  icon: string;
}

@Component({
  selector: 'app-recharge-coupon',
  standalone: true,
  imports: [
    CommonModule,
    NgbModule,
    FormSetupComponent,
    TableComponent
  ],
  templateUrl: './recharge-coupon.component.html',
  styleUrl: './recharge-coupon.component.scss'
})
export class RechargeCouponComponent implements OnInit, AfterViewInit, OnDestroy {
  siteid: string | null = null;
  siteName: string = '';
  private queryParamsSubs?: Subscription;
  private xeniaLogoDataUrl: string | null = null;

  // Tabs configuration (matching configuration.component.ts)
  tabs: CouponTab[] = [
    { id: 'generate_coupon', name: 'Generate Coupon', icon: 'fa fa-ticket' },
    { id: 'print_coupon', name: 'Print Coupon', icon: 'fa fa-print' },
    { id: 'coupon_discard', name: 'Discard Coupon', icon: 'fa fa-ban' },
    { id: 'multiple_coupon_discard', name: 'Discard Multiple', icon: 'fa fa-trash' },
    { id: 'coupon_list', name: 'Coupon List', icon: 'fa fa-table' }
  ];
  activeTab: string = 'generate_coupon';

  // Scroll navigation state
  isAtLeft = true;
  isAtRight = false;
  currentIndex = 0;
  @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef<HTMLElement>;

  // ===============================
  // GENERATE COUPON STATE & CONFIG
  // ===============================
  isGenerating: boolean = false;
  generateData = {
    coupon_nos: null as number | null,
    coupon_amount: null as number | null,
    deduction_amount: 0 as number | null
  };
  callForGenerateForm: boolean = false;
  isGenerateFormValid: boolean = false;
  generateFormConfig: any[] = [
    {
      type: 'input',
      class: 'col-12 col-sm-6 col-lg-4',
      id: 'coupon_nos',
      header: 'Number Of Coupon',
      placeholder: 'e.g. 10',
      keyRequired: true,
      required: true,
      typeText: 'number',
      checkValidation: false,
      pattern: '^[0-9]+$',
      patternMessage: 'Please enter a valid count of coupons (at least 1)',
      icon: true,
      iconName: 'fa-hashtag',
      helpText: 'Total number of coupons to generate.'
    },
    {
      type: 'input',
      class: 'col-12 col-sm-6 col-lg-4',
      id: 'coupon_amount',
      header: 'Coupon Amount (₹)',
      placeholder: 'e.g. 500',
      keyRequired: true,
      required: true,
      typeText: 'number',
      pattern: '^[0-9]+$',
      checkValidation: false,
      icon: true,
      iconName: 'fa-inr',
      helpText: 'Face value of each coupon in rupees.'
    },
    {
      type: 'input',
      class: 'col-12 col-sm-6 col-lg-4',
      id: 'deduction_amount',
      header: 'Deduction Amount (₹)',
      placeholder: 'e.g. 0',
      keyRequired: false,
      typeText: 'number',
      existingValue: 0,
      pattern: '^[0-9]+$',
      checkValidation: false,
      icon: true,
      iconName: 'fa-minus-circle',
      helpText: 'Amount to deduct from face value (must be less than coupon amount).'
    }
  ];

  // ===============================
  // PRINT COUPON STATE & CONFIG
  // ===============================
  isPrinting: boolean = false;
  printData = {
    coupon_type: 'individual_coupon',
    Date: this.formatDate(new Date())
  };
  callForPrintForm: boolean = false;
  isPrintFormValid: boolean = false;
  printFormConfig: any[] = [
    {
      type: 'date',
      class: 'col-12 col-sm-6',
      icon: true,
      iconName: 'fa-calendar',
      id: 'Date',
      header: 'Date',
      placeholder: 'YYYY-MM-DD (e.g. 2026-10-09)',
      keyRequired: true,
      required: true,
      checkValidation: false,
      existingValue: this.formatDate(new Date())
    },
    {
      type: 'auto-complete',
      class: 'col-12 col-sm-6',
      icon: true,
      iconName: 'fa-list',
      id: 'coupon_type',
      header: 'Coupon Type',
      placeholder: 'Select coupon type',
      keyRequired: true,
      required: true,
      idSelect: true,
      checkValidation: false,
      existingValue: 'individual_coupon',
      options: {
        list: [
          { id: 'individual_coupon', name: 'Individual Coupon' },
          { id: 'coupon', name: 'Prepaid Coupon' }
        ]
      }
    }
  ];

  // ===============================
  // SINGLE DISCARD STATE & CONFIG
  // ===============================
  isDiscarding = false;
  discardData = { serial_number: '' };
  callForDiscardForm = false;
  isDiscardFormValid = false;
  discardFormConfig: any[] = [
    {
      type: 'input',
      class: 'col-12',
      id: 'serial_number',
      header: 'Serial Number',
      placeholder: 'Enter coupon serial number',
      keyRequired: true,
      required: true,
      typeText: 'text',
      checkValidation: false,
      icon: true,
      iconName: 'fa-barcode',
      helpText: 'Enter the unique serial number of the coupon to discard.'
    }
  ];

  // ===============================
  // MULTIPLE DISCARD STATE & CONFIG
  // ===============================
  isMultiDiscarding = false;
  multiDiscardData = { CouponId: '', Reason: '', Password: '' };
  callForMultiDiscardForm = false;
  isMultiDiscardFormValid = false;
  multiDiscardFormConfig: any[] = [
    {
      type: 'input',
      class: 'col-12 col-sm-6 col-lg-4',
      id: 'CouponId',
      header: 'Request ID',
      placeholder: 'Enter Request ID',
      keyRequired: true,
      required: true,
      typeText: 'text',
      checkValidation: false,
      icon: true,
      iconName: 'fa-ticket',
      helpText: 'Enter the Request ID to discard Multiple Generated Coupons'
    },
    {
      type: 'input',
      class: 'col-12 col-sm-6 col-lg-4',
      id: 'Reason',
      header: 'Reason',
      placeholder: 'Enter reason for discarding',
      keyRequired: true,
      required: true,
      typeText: 'text',
      checkValidation: false,
      icon: true,
      iconName: 'fa-comment',
      helpText: 'Provide a reason for discarding the coupon(s).'
    },
    {
      type: 'input',
      class: 'col-12 col-sm-6 col-lg-4',
      id: 'Password',
      header: 'Password',
      placeholder: 'Enter your password to confirm',
      keyRequired: true,
      required: true,
      typeText: 'password',
      checkValidation: false,
      icon: true,
      iconName: 'fa-lock',
      helpText: 'Enter your account password to authorise this action.'
    }
  ];

  // ===============================
  // COUPON RECORDS TABLE STATE
  // ===============================
  isLoadingRecords: boolean = false;
  lastGeneratedResult: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiCallsService,
    private toast: ToastService,
    private agGridService: AgGridTableService,
    private tableSchemaService: TableSchemaService
  ) { }

  ngOnInit(): void {
    this.resolveSiteContext();
    this.resolveInitialTab();
    this.loadCouponRecords();

    this.queryParamsSubs = this.route.queryParams.subscribe((params) => {
      this.resolveSiteContext();
      if (params['tab'] && this.tabs.some(t => t.id === params['tab'])) {
        this.activeTab = params['tab'];
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updateEdgeState(), 100);
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateEdgeState();
  }

  ngOnDestroy(): void {
    this.queryParamsSubs?.unsubscribe();
  }

  // ===============================
  // TABBING & SCROLL NAVIGATION
  // ===============================
  private resolveInitialTab(): void {
    const queryTab = this.route.snapshot.queryParamMap.get('tab');
    if (queryTab && this.tabs.some(t => t.id === queryTab)) {
      this.activeTab = queryTab;
      return;
    }
    const path = this.route.snapshot.routeConfig?.path || '';
    if (path === 'coupon_discard' || path.includes('discard')) {
      this.activeTab = 'coupon_discard';
    } else if (path === 'multiple_coupon_discard') {
      this.activeTab = 'multiple_coupon_discard';
    } else if (path === 'print_coupon') {
      this.activeTab = 'print_coupon';
    } else if (path === 'coupon_list') {
      this.activeTab = 'coupon_list';
    } else {
      this.activeTab = 'generate_coupon';
    }
  }

  onTabChange(event: any): void {
    this.activeTab = event.nextId;
    if (event.nextId === 'coupon_list') {
      this.loadCouponRecords();
    }
  }

  private getNavItems(): HTMLElement[] {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return [];
    return Array.from(el.querySelectorAll('.nav-item')) as HTMLElement[];
  }

  scrollRight(): void {
    const container = this.scrollContainer?.nativeElement;
    const items = this.getNavItems();
    if (!container || items.length === 0) return;

    const nextIndex = Math.min(this.currentIndex + 1, items.length - 1);
    const target = items[nextIndex];
    const newScroll = target.offsetLeft - (container.clientWidth - target.offsetWidth) / 2;

    container.scrollTo({ left: Math.max(0, newScroll), behavior: 'smooth' });
    this.currentIndex = nextIndex;
    setTimeout(() => this.updateEdgeState(), 300);
  }

  scrollLeft(): void {
    const container = this.scrollContainer?.nativeElement;
    const items = this.getNavItems();
    if (!container || items.length === 0) return;

    const prevIndex = Math.max(this.currentIndex - 1, 0);
    const target = items[prevIndex];
    const newScroll = target.offsetLeft - (container.clientWidth - target.offsetWidth) / 2;

    container.scrollTo({ left: Math.max(0, newScroll), behavior: 'smooth' });
    this.currentIndex = prevIndex;
    setTimeout(() => this.updateEdgeState(), 300);
  }

  private updateEdgeState(): void {
    const el = this.scrollContainer?.nativeElement;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    this.isAtLeft = el.scrollLeft <= 0;
    this.isAtRight = el.scrollLeft >= maxScroll - 1;
  }

  // ===============================
  // SITE RESOLUTION & DECODING
  // ===============================
  decodeSiteId(val: string | null | undefined): string {
    if (!val) return '';
    const trimmed = String(val).trim();
    try {
      const decoded = atob(trimmed);
      if (decoded) {
        return decoded.trim();
      }
    } catch {
      // not base64 encoded
    }
    return trimmed;
  }

  private resolveSiteContext(): void {
    const rawParam =
      this.route.snapshot.queryParamMap.get('siteid') ||
      this.route.snapshot.queryParamMap.get('site_id');
    const rawLogin =
      localStorage.getItem('site_idLogin') ||
      localStorage.getItem('siteId') ||
      localStorage.getItem('site_id') ||
      sessionStorage.getItem('consumerSiteId');
    const candidate = rawParam || rawLogin;

    if (candidate) {
      const resolved = this.decodeSiteId(candidate);
      this.siteid = resolved && resolved !== 'ALL' && resolved !== 'all' ? resolved : null;
    } else {
      this.siteid = null;
    }

    this.siteName = this.resolveSiteName(this.siteid || '');
  }

  private resolveSiteName(siteId: string = ''): string {
    if (!siteId) return '';
    try {
      const storedSites = localStorage.getItem('siteList');
      if (storedSites) {
        const parsed = JSON.parse(storedSites);
        const siteList = Array.isArray(parsed) ? parsed : parsed?.Data?.list || [];
        const matched = siteList.find((s: any) => String(s.site_id) === String(siteId) || String(s.id) === String(siteId));
        if (matched?.site_name) return matched.site_name;
        if (matched?.facility_name) return matched.facility_name;
        if (matched?.name) return matched.name;
      }
    } catch {
      // ignore
    }
    const directName = localStorage.getItem('site_name') || localStorage.getItem('SiteName');
    if (directName && directName !== 'undefined') return directName;
    return '';
  }

  formatDate(date: any): string {
    if (!date) return '';
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date.trim())) {
      return date.trim();
    }
    const d = new Date(date);
    if (isNaN(d.getTime())) return String(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  // ===============================
  // GENERATE COUPON HANDLERS
  // ===============================
  triggerGenerateSubmit(): void {
    if (!this.siteid) {
      this.toast.showToast('Site ID is missing. Please select a site from the header dropdown.', 'colored', 5000, 'warning');
      return;
    }
    this.callForGenerateForm = true;
    setTimeout(() => {
      this.callForGenerateForm = false;
    }, 0);
  }

  onGenerateFormCheck(isValid: boolean): void {
    this.isGenerateFormValid = isValid;
  }

  onGenerateFormEmitted(payload: { id: string; value: any }): void {
    const val = payload?.value;
    if (val) {
      if (val.coupon_nos !== undefined && val.coupon_nos !== null) {
        this.generateData.coupon_nos = Number(val.coupon_nos);
      }
      if (val.coupon_amount !== undefined && val.coupon_amount !== null) {
        this.generateData.coupon_amount = Number(val.coupon_amount);
      }
      if (val.deduction_amount !== undefined && val.deduction_amount !== null) {
        this.generateData.deduction_amount = Number(val.deduction_amount);
      }
    }
    this.onSubmitGenerate();
  }

  onSubmitGenerate(): void {
    if (!this.siteid) {
      this.toast.showToast('Site ID is missing. Please select a site from the header dropdown.', 'colored', 5000, 'warning');
      return;
    }

    const couponNos = Number(this.generateData.coupon_nos);
    if (!couponNos || couponNos <= 0 || !Number.isInteger(couponNos)) {
      this.toast.showToast('Please enter a valid number of coupons (greater than 0)', 'colored', 4000, 'warning');
      return;
    }

    const couponAmount = Number(this.generateData.coupon_amount);
    if (!couponAmount || couponAmount <= 0) {
      this.toast.showToast('Please enter a valid coupon amount (greater than 0)', 'colored', 4000, 'warning');
      return;
    }

    const deductionAmount = Number(this.generateData.deduction_amount ?? 0);
    if (deductionAmount < 0) {
      this.toast.showToast('Deduction amount cannot be negative', 'colored', 4000, 'warning');
      return;
    }

    if (deductionAmount >= couponAmount) {
      this.toast.showToast('Deduction amount must be less than coupon amount', 'colored', 4000, 'warning');
      return;
    }

    Swal.fire({
      title: 'Generate Coupon?',
      text: `Generate ${couponNos} coupon(s) of amount ₹${couponAmount} with deduction ₹${deductionAmount}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Generate',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    }).then((result) => {
      if (result.isConfirmed) {
        this.executeGenerateCoupon();
      }
    });
  }

  private executeGenerateCoupon(): void {
    this.isGenerating = true;

    const formData = new FormData();
    formData.append('coupon_nos', String(this.generateData.coupon_nos));
    formData.append('coupon_amount', String(this.generateData.coupon_amount));
    formData.append('deduction_amount', String(this.generateData.deduction_amount ?? 0));
    formData.append('site_id', this.siteid || '');

    this.apiService.generateCoupon(formData).subscribe({
      next: (res) => {
        this.isGenerating = false;
        if (res?.Status === 'Error' || res?.status === 'Error' || res?.status === false) {
          this.toast.showToast(res?.Message || res?.message || 'Failed to generate coupon', 'colored', 5000, 'danger');
          return;
        }

        this.lastGeneratedResult = res?.Data || res;
        this.toast.showToast(res?.Message || res?.message || 'Coupons generated successfully', 'colored', 5000, 'success');

        Swal.fire({
          title: 'Coupons Generated!',
          text: res?.Message || `${this.generateData.coupon_nos} coupon(s) generated successfully.`,
          icon: 'success',
          confirmButtonText: 'OK'
        });

        this.generateData = {
          coupon_nos: null,
          coupon_amount: null,
          deduction_amount: 0
        };

        this.loadCouponRecords();
      },
      error: (err) => {
        this.isGenerating = false;
        const msg = err?.error?.Message || err?.error?.message || 'Failed to generate coupon. Please try again.';
        this.toast.showToast(msg, 'colored', 5000, 'danger');
      }
    });
  }

  // ===============================
  // PRINT COUPON HANDLERS
  // ===============================
  triggerPrintSubmit(): void {
    if (!this.siteid) {
      this.toast.showToast('Site ID is missing. Please select a site from the header dropdown.', 'colored', 5000, 'warning');
      return;
    }
    this.callForPrintForm = true;
    setTimeout(() => {
      this.callForPrintForm = false;
    }, 0);
  }

  onPrintFormCheck(isValid: boolean): void {
    this.isPrintFormValid = isValid;
  }

  onPrintFormEmitted(payload: { id: string; value: any }): void {
    const val = payload?.value;
    if (val) {
      if (val.coupon_type) {
        this.printData.coupon_type = val.coupon_type;
      }
      if (val.Date) {
        this.printData.Date = this.formatDate(val.Date);
      }
    }
    this.onPrintCoupon();
  }

  onPrintCoupon(): void {
    if (!this.siteid) {
      this.toast.showToast('Site ID is missing. Please select a site from the header dropdown.', 'colored', 5000, 'warning');
      return;
    }

    if (!this.printData.coupon_type) {
      this.toast.showToast('Please select a coupon type', 'colored', 4000, 'warning');
      return;
    }

    if (!this.printData.Date) {
      this.toast.showToast('Please select a date', 'colored', 4000, 'warning');
      return;
    }

    const formattedDate = this.formatDate(this.printData.Date);
    this.isPrinting = true;
    const params = {
      site_id: this.siteid,
      coupon_type: this.printData.coupon_type,
      Date: formattedDate
    };

    this.apiService.printCouponBlob(params).subscribe({
      next: (blob: Blob) => {
        this.isPrinting = false;
        if (blob.type === 'application/json' || blob.type.includes('json')) {
          blob.text().then((text) => {
            try {
              const jsonRes = JSON.parse(text);
              if (jsonRes?.Status === 'Error' || jsonRes?.status === 'Error' || jsonRes?.status === false) {
                this.toast.showToast(jsonRes?.Message || jsonRes?.message || 'Failed to print coupons', 'colored', 5000, 'danger');
                return;
              }
              this.handleJsonPrintResponse(jsonRes);
            } catch {
              this.handleBlobPrint(blob);
            }
          });
        } else {
          this.handleBlobPrint(blob);
        }
      },
      error: (err) => {
        this.isPrinting = false;
        if (err?.error instanceof Blob) {
          err.error.text().then((text: string) => {
            try {
              const parsed = JSON.parse(text);
              this.toast.showToast(parsed?.Message || parsed?.message || 'Failed to print coupons', 'colored', 5000, 'danger');
            } catch {
              this.toast.showToast('Failed to print coupons', 'colored', 5000, 'danger');
            }
          });
        } else {
          const msg = err?.error?.Message || err?.error?.message || 'Failed to print coupons';
          this.toast.showToast(msg, 'colored', 5000, 'danger');
        }
      }
    });
  }

  private handleBlobPrint(blob: Blob): void {
    const fileUrl = URL.createObjectURL(blob);
    const printWindow = window.open(fileUrl, '_blank');
    if (!printWindow) {
      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = `coupons_${this.printData.coupon_type}_${this.formatDate(this.printData.Date)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    this.toast.showToast('Print request completed successfully', 'colored', 4000, 'success');
  }

  private handleJsonPrintResponse(res: any): void {
    const couponRows = this.getCouponRows(res);
    if (couponRows.length) {
      this.createCouponPdf(couponRows);
      return;
    }
    const url = res?.Data?.url || res?.Data?.file_url || res?.url;
    if (url) {
      window.open(url, '_blank');
      this.toast.showToast('Print file opened in new tab', 'colored', 4000, 'success');
    } else {
      this.toast.showToast(res?.Message || res?.message || 'Print coupon request processed', 'colored', 5000, 'info');
    }
  }

  private getCouponRows(res: any): any[] {
    const data = res?.Data ?? res?.data ?? res;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.list)) return data.list;
    if (Array.isArray(data?.coupons)) return data.coupons;
    if (data && typeof data === 'object' && data.id != null) return [data];
    return [];
  }

  private async createCouponPdf(coupons: any[]): Promise<void> {
    await this.loadXeniaLogo();

    if (this.printData.coupon_type === 'individual_coupon') {
      this.createIndividualCouponPdf(coupons);
      return;
    }

    const rows: any[] = [];

    for (let i = 0; i < coupons.length; i += 2) {
      rows.push({
        columns: [
          this.couponPdfCard(coupons[i]),
          coupons[i + 1]
            ? this.couponPdfCard(coupons[i + 1])
            : { text: '', width: '*' }
        ],
        columnGap: 12,
        margin: [0, 0, 0, 12],
        unbreakable: true
      });
    }

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [42, 72, 42, 36],

      header: (currentPage: number, pageCount: number) => ({
        columns: [
          {
            text: `Run Date :: ${this.formatPrintDate(new Date())}`,
            bold: true,
            fontSize: 9,
            width: '*'
          },
          {
            text: 'Prepaid Coupons',
            bold: true,
            color: '#123f70',
            fontSize: 16,
            alignment: 'center',
            width: '*'
          },
          {
            text: `Page ${currentPage} of ${pageCount}`,
            bold: true,
            fontSize: 9,
            alignment: 'right',
            width: '*'
          }
        ],
        margin: [42, 28, 42, 0]
      }),

      content: rows,
      defaultStyle: { font: 'Roboto' }
    };

    pdfMake.createPdf(docDefinition).open();

    this.toast.showToast(
      'Coupon PDF created successfully',
      'colored',
      4000,
      'success'
    );
  }

  private createIndividualCouponPdf(coupons: any[]): void {
    const content: any[] = [];
    coupons.forEach((coupon, index) => {
      if (index > 0) content.push({ text: '', pageBreak: 'before' });
      content.push(this.individualCouponCard(coupon));
    });

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [42, 42, 42, 42],
      content,
      defaultStyle: { font: 'Roboto' }
    };
    pdfMake.createPdf(docDefinition).open();
    this.toast.showToast('Coupon PDF created successfully', 'colored', 4000, 'success');
  }

  private individualCouponCard(coupon: any): any {
    const value = (input: any) => input == null || String(input).trim() === '' ? '' : String(input).trim();
    const siteName = value(coupon.site_name);
    const groupName = value(coupon.group_name) || siteName;
    const address = [coupon.address, coupon.city].map(value).filter(Boolean).join(', ');
    const amount = value(coupon.coupon_amount ?? coupon.net_amount);
    const runDate = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date());

    return {
      stack: [
        { text: 'Prepaid Coupon', alignment: 'center', bold: true, color: '#123f70', fontSize: 24, margin: [0, 0, 0, 12] },
        {
          margin: [0, 0, 0, 0],
          table: {
            widths: [28, '*'],
            body: [[
              { text: `Rs. ${amount}`, rotation: 270, bold: true, fontSize: 16, alignment: 'center', verticalAlignment: 'middle', margin: [0, 10, 0, 10] },
              {
                table: {
                  widths: [190, '*'],
                  body: [
                    [
                      { text: 'S.No.', bold: true, alignment: 'center', fontSize: 14, margin: [0, 8] },
                      { text: value(coupon.serial_number), bold: true, alignment: 'right', fontSize: 14, margin: [0, 8] }
                    ],
                    [
                      { text: 'Coupon No.', bold: true, alignment: 'center', fontSize: 14, margin: [0, 8] },
                      { text: value(coupon.id ?? coupon.coupon_no ?? coupon.coupon_number), bold: true, alignment: 'right', fontSize: 14, margin: [0, 8] }
                    ],
                    [{ text: groupName, colSpan: 2, bold: true, italics: true, color: '#a10d0d', alignment: 'center', fontSize: 21, margin: [0, 8] }, ''],
                    [
                      { text: siteName || address, bold: true, alignment: 'center', fontSize: 14, margin: [0, 12] },
                      this.xeniaLogoDataUrl
                        ? {
                          image: this.xeniaLogoDataUrl,
                          rowSpan: 2,
                          fit: [105, 55],
                          alignment: 'center',
                          margin: [4, 15, 4, 10]
                        }
                        : {
                          text: 'XENIA',
                          rowSpan: 2,
                          bold: true,
                          color: '#17628c',
                          alignment: 'center',
                          fontSize: 22,
                          margin: [0, 20]
                        }],
                    [{ text: address, bold: true, alignment: 'center', fontSize: 15, margin: [0, 9] }, ''],
                    [
                      { text: amount, bold: true, alignment: 'right', fontSize: 14, margin: [0, 8] },
                      { text: `Dial: ${value(coupon.dial_no)}`, bold: true, fontSize: 14, margin: [6, 8] }
                    ],
                    [
                      { text: 'Flat No.', bold: true, fontSize: 14, margin: [0, 8] },
                      { text: value(coupon.flat_no ?? coupon.flat_number ?? coupon.location_no), bold: true, fontSize: 14, margin: [6, 8] }
                    ],
                    [
                      { text: `Run Date : ${runDate}`, bold: true, fontSize: 11, margin: [0, 7] },
                      { text: 'Powered by Radius', bold: true, alignment: 'center', fontSize: 10, margin: [0, 7] }
                    ]
                  ]
                },
                layout: {
                  hLineWidth: (i: number) => i === 2 ? 1.5 : 0.8,
                  vLineWidth: () => 0.8,
                  hLineColor: (i: number) => i === 2 ? '#0875d1' : '#111111',
                  vLineColor: () => '#111111',
                  paddingLeft: () => 7,
                  paddingRight: () => 7,
                  paddingTop: () => 3,
                  paddingBottom: () => 3
                }
              }
            ]]
          },
          layout: {
            hLineWidth: () => 0.8,
            vLineWidth: () => 0.8,
            hLineColor: () => '#111111',
            vLineColor: () => '#111111',
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0
          }
        }
      ]
    };
  }

  private couponPdfCard(coupon: any): any {
    const text = (value: any) => value == null || String(value).trim() === '' ? '' : String(value).trim();
    const amount = text(coupon.coupon_amount ?? coupon.net_amount);
    const address = [coupon.address, coupon.city].map(text).filter(Boolean).join(', ');
    const cardRows = [
      [
        this.xeniaLogoDataUrl
          ? {
            image: this.xeniaLogoDataUrl,
            rowSpan: 2,
            fit: [75, 35],
            alignment: 'center',
            margin: [2, 8, 2, 5]
          }
          : {
            text: text(coupon.site_name) || 'XENIA',
            rowSpan: 2,
            bold: true,
            color: '#174f86',
            fontSize: 11,
            alignment: 'center',
            margin: [0, 5, 0, 0]
          },
        {
          text: text(coupon.serial_number),
          bold: true,
          alignment: 'center',
          fontSize: 11
        },
        {
          text: text(
            coupon.id ?? coupon.coupon_no ?? coupon.coupon_number
          ),
          bold: true,
          alignment: 'center',
          fontSize: 10
        }
      ],
      [
        '',
        {
          text: 'S.No.',
          bold: true,
          alignment: 'center',
          fontSize: 8
        },
        {
          text: 'Coupon No.',
          bold: true,
          alignment: 'center',
          fontSize: 8
        }
      ],
      [
        {
          text: text(coupon.group_name) || text(coupon.site_name),
          colSpan: 3,
          bold: true,
          italics: true,
          color: '#9b1717',
          alignment: 'center',
          fontSize: 13,
          margin: [0, 3]
        },
        '',
        ''
      ],
      [
        {
          text: [
            text(coupon.address),
            text(coupon.city)
          ].filter(Boolean).join(', '),
          colSpan: 3,
          bold: true,
          alignment: 'center',
          fontSize: 9,
          margin: [0, 3]
        },
        '',
        ''
      ],
      [
        {
          text: amount,
          bold: true,
          alignment: 'right',
          fontSize: 10
        },
        {
          text: `Dial: ${text(coupon.dial_no)}`,
          colSpan: 2,
          bold: true,
          fontSize: 9,
          margin: [4, 1]
        },
        ''
      ],
      [
        {
          text: 'Comments:',
          bold: true,
          fontSize: 9,
          margin: [0, 6]
        },
        {
          text: '',
          colSpan: 2,
          margin: [0, 4],
          border: [true, true, true, true]
        },
        ''
      ]
    ];
    return {
      width: '*',
      table: { widths: ['34%', '29%', '37%'], body: cardRows },
      layout: {
        hLineWidth: (i: number) => i === 2 ? 2 : 0.8,
        vLineWidth: () => 0.8,
        hLineColor: (i: number) => i === 2 ? '#0875d1' : '#111111',
        vLineColor: () => '#111111',
        paddingLeft: () => 3,
        paddingRight: () => 3,
        paddingTop: () => 2,
        paddingBottom: () => 2
      }
    };
  }

  private formatPrintDate(date: Date): string {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    }).format(date);
  }

  // ===============================
  // SINGLE DISCARD HANDLERS
  // ===============================
  triggerDiscardSubmit(): void {
    if (!this.siteid) {
      this.toast.showToast('Site ID is missing. Please select a site from the header dropdown.', 'colored', 5000, 'warning');
      return;
    }
    this.callForDiscardForm = true;
    setTimeout(() => {
      this.callForDiscardForm = false;
    }, 0);
  }

  onDiscardFormCheck(isValid: boolean): void {
    this.isDiscardFormValid = isValid;
  }

  onDiscardFormEmitted(payload: { id: string; value: any }): void {
    const val = payload?.value;
    if (val?.serial_number) {
      this.discardData.serial_number = String(val.serial_number).trim();
    }
    this.executeDiscard();
  }

  private executeDiscard(): void {
    if (!this.discardData.serial_number) {
      this.toast.showToast('Please enter a valid serial number.', 'colored', 4000, 'warning');
      return;
    }
    Swal.fire({
      title: 'Discard Coupon?',
      text: `Discard coupon with serial number: ${this.discardData.serial_number}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Discard',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d'
    }).then((result) => {
      if (!result.isConfirmed) return;
      this.isDiscarding = true;
      const fd = new FormData();
      fd.append('site_id', this.siteid || '');
      fd.append('serial_number', this.discardData.serial_number);
      this.apiService.couponDiscard(fd).subscribe({
        next: (res: any) => {
          this.isDiscarding = false;
          if (res?.Status === 'Error' || res?.status === false) {
            this.toast.showToast(res?.Message || 'Failed to discard coupon', 'colored', 5000, 'danger');
            return;
          }
          this.toast.showToast(res?.Message || 'Coupon discarded successfully', 'colored', 5000, 'success');
          Swal.fire({ title: 'Discarded!', text: res?.Message || 'Coupon discarded.', icon: 'success', confirmButtonText: 'OK' });
          this.discardData.serial_number = '';
          this.loadCouponRecords();
        },
        error: (err: any) => {
          this.isDiscarding = false;
          this.toast.showToast(err?.error?.Message || 'Failed to discard coupon.', 'colored', 5000, 'danger');
        }
      });
    });
  }

  // ===============================
  // MULTIPLE DISCARD HANDLERS
  // ===============================
  triggerMultiDiscardSubmit(): void {
    if (!this.siteid) {
      this.toast.showToast('Site ID is missing. Please select a site from the header dropdown.', 'colored', 5000, 'warning');
      return;
    }
    this.callForMultiDiscardForm = true;
    setTimeout(() => {
      this.callForMultiDiscardForm = false;
    }, 0);
  }

  onMultiDiscardFormCheck(isValid: boolean): void {
    this.isMultiDiscardFormValid = isValid;
  }

  onMultiDiscardFormEmitted(payload: { id: string; value: any }): void {
    const val = payload?.value;
    if (val) {
      if (val.CouponId) this.multiDiscardData.CouponId = String(val.CouponId).trim();
      if (val.Reason) this.multiDiscardData.Reason = String(val.Reason).trim();
      if (val.Password) this.multiDiscardData.Password = String(val.Password).trim();
    }
    this.executeMultiDiscard();
  }

  private executeMultiDiscard(): void {
    if (!this.multiDiscardData.CouponId) {
      this.toast.showToast('Please enter coupon ID(s) to discard.', 'colored', 4000, 'warning');
      return;
    }
    Swal.fire({
      title: 'Discard Multiple Coupons?',
      text: `Discard coupon(s): ${this.multiDiscardData.CouponId}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Discard All',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d'
    }).then((result) => {
      if (!result.isConfirmed) return;
      this.isMultiDiscarding = true;
      const fd = new FormData();
      fd.append('site_id', this.siteid || '');
      fd.append('CouponId', this.multiDiscardData.CouponId);
      fd.append('Reason', this.multiDiscardData.Reason);
      fd.append('Password', this.multiDiscardData.Password);
      this.apiService.multipleCouponDiscard(fd).subscribe({
        next: (res: any) => {
          this.isMultiDiscarding = false;
          if (res?.Status === 'Error' || res?.status === false) {
            this.toast.showToast(res?.Message || 'Failed to discard coupons', 'colored', 5000, 'danger');
            return;
          }
          this.toast.showToast(res?.Message || 'Coupons discarded successfully', 'colored', 5000, 'success');
          Swal.fire({ title: 'Discarded!', text: res?.Message || 'Coupons discarded.', icon: 'success', confirmButtonText: 'OK' });
          this.multiDiscardData = { CouponId: '', Reason: '', Password: '' };
          this.loadCouponRecords();
        },
        error: (err: any) => {
          this.isMultiDiscarding = false;
          this.toast.showToast(err?.error?.Message || 'Failed to discard coupons.', 'colored', 5000, 'danger');
        }
      });
    });
  }

  private async loadXeniaLogo(): Promise<string | null> {
    if (this.xeniaLogoDataUrl) {
      return this.xeniaLogoDataUrl;
    }

    try {
      const response = await fetch(
        '/assets/DashboardIcon/radius_logo.png'
      );

      if (!response.ok) {
        throw new Error('Failed to load Xenia logo');
      }

      const blob = await response.blob();

      this.xeniaLogoDataUrl = await new Promise<string>(
        (resolve, reject) => {
          const reader = new FileReader();

          reader.onload = () => {
            if (typeof reader.result === 'string') {
              resolve(reader.result);
            } else {
              reject(new Error('Invalid logo data'));
            }
          };

          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        }
      );

      return this.xeniaLogoDataUrl;
    } catch (error) {
      console.error('Unable to load Xenia logo:', error);
      return null;
    }
  }
  // ===============================
  // COUPON RECORDS TABLE (API)
  // ===============================
  loadCouponRecords(): void {
    if (!this.siteid) {
      this.agGridService.processTableData({ Data: { header: [], list: [] } });
      return;
    }

    this.isLoadingRecords = true;
    const payload = { site_id: this.siteid };

    this.apiService.CouponRecord(payload).subscribe({
      next: (res: any) => {
        this.isLoadingRecords = false;
        if (res?.Status === 'Error' || res?.status === 'Error' || res?.status === false) {
          this.toast.showToast(res?.Message || res?.message || 'Failed to load coupon records', 'colored', 5000, 'warning');
          this.agGridService.processTableData({ Data: { header: [], list: [] } });
          return;
        }

        const dataObj = res?.Data || res || {};
        const list = Array.isArray(dataObj?.list)
          ? dataObj.list
          : (Array.isArray(dataObj) ? dataObj : (Array.isArray(res) ? res : []));

        const columns = dataObj?.columns || dataObj?.list?.columns || res?.columns || res?.list?.columns;
        let headers = dataObj?.header || res?.header;

        if (columns && typeof columns === 'object') {
          headers = this.tableSchemaService.buildHeaderFromColumns(columns);
        } else if (!headers || !headers.length) {
          if (list && list.length > 0) {
            const inferredCols: Record<string, any> = {};
            Object.keys(list[0]).forEach((k) => {
              if (k !== 'isDummy' && k !== '_id') {
                inferredCols[k] = {
                  title: k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                  show: true,
                  download: true
                };
              }
            });
            headers = this.tableSchemaService.buildHeaderFromColumns(inferredCols);
          } else {
            headers = [];
          }
        }

        const tablePayload = {
          ...res,
          Data: {
            ...dataObj,
            header: headers,
            list: list
          }
        };

        this.agGridService.processTableData(tablePayload);
      },
      error: (err: any) => {
        this.isLoadingRecords = false;
        const msg = err?.error?.Message || err?.error?.message || 'Failed to fetch coupon records';
        this.toast.showToast(msg, 'colored', 5000, 'warning');
        this.agGridService.processTableData({ Data: { header: [], list: [] } });
      }
    });
  }
}
